<template>
  <wui-card
    class="no-padding my-2 min-w-0 max-w-full w-fit px-4 pt-4 pb-1"
    variant="neut"
    state="tonal"
    shadow="raised"
  >
    <p
      v-if="isScheduleCancelled"
      class="text-label-r-13-18 text-fg-base-fg10 mb-3 text-center"
      data-test="event-card-schedule-cancelled"
    >
      {{ $t('chat.eventHasBeenDeleted') }}
    </p>
    <p
      v-else-if="isScheduleUpdated"
      class="text-label-r-13-18 text-fg-base-fg10 mb-3 text-center"
      data-test="event-card-schedule-updated-by"
    >
      {{ $t('chat.eventScheduleUpdatedBy', { name: editorDisplayName }) }}
    </p>
    <div class="flex items-start gap-2 mb-3">
      <p
        class="text-heading-sb-18-24 text-fg-base-fg3 max-w-[300px] line-clamp-3 text-ellipsis min-w-0 grow"
        :class="[
          formattedEvent?.displaySummary.trim().includes(' ') ? 'break-keep' : 'break-all',
          { 'line-through opacity-70': isScheduleCancelled },
        ]"
        :title="formattedEvent?.displaySummary ?? ''"
        data-test="event-card-summary"
      >
        {{ formattedEvent?.displaySummary }}
      </p>
      <span
        v-if="!isScheduleCancelled"
        class="inline-flex shrink-0"
        :title="canEditEvent ? $t('common.editEvent') : $t('common.youCantEditEvent')"
      >
        <wui-btn
          icon
          variant="neut"
          state="alpha"
          :size="32"
          prepend-icon="editM"
          data-test="event-card-edit-button"
          :disabled="!canEditEvent"
          @click="onEditClick"
        />
      </span>
    </div>
    <div class="flex items-center gap-3 mb-3 max-w-[300px]">
      <p
        class="text-body-r-14-20 text-fg-base-fg10 line-clamp-5 text-ellipsis whitespace-pre-line"
        :class="formattedEvent?.description?.trim().includes(' ') ? 'break-keep' : 'break-all'"
        :title="formattedEvent?.description || '-'"
      >
        {{ formattedEvent?.description || '-' }}
      </p>
    </div>
    <div class="flex items-center gap-3 mb-3">
      <wui-icon
        name="userM"
        class="text-fg-base-fg17 h-5! w-5!"
      />
      <p class="text-body-r-14-20 text-fg-base-fg10">
        {{ $t('common.organizer') }}: {{ formattedEvent?.organizer?.name || editorDisplayName }}
      </p>
    </div>
    <div
      v-if="eventParticipantsAsContacts.length"
      class="flex items-start gap-3 mb-3 min-w-0 max-w-[300px]"
      data-test="event-card-participants"
    >
      <wui-icon
        name="userGroupM"
        class="text-fg-base-fg17 h-5! w-5! shrink-0 mt-0.5"
      />
      <div class="min-w-0">
        <p class="text-body-r-14-20 text-fg-base-fg10 mb-1">
          {{ $t('common.participants') }} ({{ eventParticipantsAsContacts.length }})
        </p>
        <p
          class="text-body-r-14-20 text-fg-base-fg10 line-clamp-4 text-ellipsis wrap-break-word"
          :title="participantsNames"
        >
          {{ participantsNames }}
        </p>
      </div>
    </div>
    <div class="flex items-center gap-3 mb-3">
      <wui-icon
        name="calendarM"
        class="text-fg-base-fg17 h-5! w-5!"
      />
      <p
        class="text-body-r-14-20 text-fg-base-fg10"
        :class="{ 'line-through opacity-70': isScheduleCancelled }"
        data-test="event-card-start-time"
      >
        {{ $t('common.eventStart') }}: {{ formattedEvent?.formattedDate }} {{ $t('calendar.in') }} {{ formattedEvent?.formattedTime }}
      </p>
    </div>
    <div
      v-if="formattedEvent?.formattedUntilDate"
      class="flex items-center gap-3 mb-3"
    >
      <wui-icon
        name="calendarM"
        class="text-fg-base-fg17 h-5! w-5!"
      />
      <p
        class="text-body-r-14-20 text-fg-base-fg10"
        :class="{ 'line-through opacity-70': isScheduleCancelled }"
        data-test="event-card-end-date"
      >
        {{ $t('common.eventEnd') }}: {{ formattedEvent.formattedUntilDate }}
      </p>
    </div>
    <div class="flex items-center gap-3 mb-3">
      <wui-icon
        name="clockM"
        class="text-fg-base-fg17 h-5! w-5!"
      />
      <p
        class="text-body-r-14-20 text-fg-base-fg10"
        :class="{ 'line-through opacity-70': isScheduleCancelled }"
      >
        {{ $t('common.duration') }}: {{ formattedEvent?.formattedDuration }}
      </p>
    </div>
    <div
      v-if="formattedEvent?.formattedRecurrenceRule"
      class="flex items-center gap-3 mb-3 max-w-[300px]"
    >
      <wui-icon
        name="repeatM"
        class="text-fg-base-fg17 h-5! w-5!"
      />
      <p
        class="text-body-r-14-20 text-fg-base-fg10"
        :class="{ 'line-through opacity-70': isScheduleCancelled }"
      >
        {{ formattedEvent?.formattedRecurrenceRule }} {{ $t('calendar.in') }} {{ formattedEvent?.formattedTime }}
      </p>
    </div>
    <div
      v-if="formattedEvent?.url && !isScheduleCancelled"
      ref="conferenceLinkRowRef"
      class="flex items-center gap-3 mb-3"
    >
      <wui-icon
        name="linkM"
        class="text-fg-base-fg17 h-5! w-5!"
      />
      <button
        v-if="formattedEvent?.location"
        type="button"
        class="text-body-r-14-20 text-comp-link-neut-def cursor-pointer border-0 bg-transparent p-0"
        data-test="event-card-conference-link"
        @click="copyLink"
      >
        {{ $t('calls.conferenceLink') }}
      </button>
      <wui-btn
        icon
        variant="neut"
        state="alpha"
        :size="32"
        :prepend-icon="isLinkCopied ? 'checkRegularM' : 'copyM'"
        data-test="event-card-copy-link"
        @click="copyLink"
      />
    </div>
    <teleport to="body">
      <p
        v-if="isLinkCopied"
        class="pointer-events-none fixed z-50 max-w-[min(100vw-16px,280px)] text-center text-1316 bg-elev-context-menu-bg text-fg-base-fg3 p-3 rounded-12 shadow-overlay"
        data-test="event-card-link-copied"
        :style="linkCopiedTooltipStyle"
      >
        {{ $t('common.linkCopiedToClipboard') }}
      </p>
    </teleport>
  </wui-card>
</template>

<script setup lang="ts">
import { WuiBtn, WuiCard, WuiIcon } from '@wui/common-library'
import {
  ConferenceScheduleChangedByOtherEvent,
  ConferenceScheduleChangedByYouEvent,
  ConferenceScheduleDisabledByOtherEvent,
  ConferenceScheduleDisabledByYouEvent,
  ConferenceScheduleUpdatedByOtherEvent,
  ConferenceScheduleUpdatedByYouEvent,
  HistoryMessageItem,
} from '@wui/im'
import { computed, nextTick, onBeforeUnmount, ref } from 'vue'


import { useEventFormatting, type FormattedEvent } from '@/features/group-events-list'

import { useStoreContacts } from '@/entities/contact'
import { useStoreRooms, useModeratedRoom } from '@/entities/room'
import {
  useCalendarAttendeeContacts,
  useRoomScheduleStore,
} from '@/entities/schedule'

import { useCurrentUser } from '@/shared/composables/current-user'
import { useLocalization } from '@/shared/composables/localization'
import { Contact } from '@/shared/types/api'

const props = defineProps<{
    scheduledEvent: HistoryMessageItem<
      | ConferenceScheduleChangedByOtherEvent
      | ConferenceScheduleChangedByYouEvent
      | ConferenceScheduleDisabledByOtherEvent
      | ConferenceScheduleDisabledByYouEvent
      | ConferenceScheduleUpdatedByOtherEvent
      | ConferenceScheduleUpdatedByYouEvent
    >
}>()

const emit = defineEmits<{
  edit: [event: FormattedEvent]
}>()

const { formatEvents } = useEventFormatting()
const { t } = useLocalization()
const { currentUser } = useCurrentUser()
const { findByPServed: findRoom } = useStoreRooms()
const { findByPServed } = useStoreContacts()
const { resolveAttendees } = useCalendarAttendeeContacts()
const roomScheduleStore = useRoomScheduleStore()


const isLinkCopied = ref(false)
const conferenceLinkRowRef = ref<HTMLElement | null>(null)
const linkCopiedTooltipStyle = ref<Record<string, string>>({
  top: '0px',
  left: '0px',
  transform: 'translate(-50%, calc(-100% - 8px))',
  visibility: 'hidden',
})
let copiedTimeout: ReturnType<typeof setTimeout> | undefined

const updateLinkCopiedTooltipPosition = () => {
  const row = conferenceLinkRowRef.value
  if (!row) {
    return
  }

  const rect = row.getBoundingClientRect()
  linkCopiedTooltipStyle.value = {
    top: `${rect.top}px`,
    left: `${rect.left + rect.width / 2}px`,
    transform: 'translate(-50%, calc(-100% - 8px))',
    visibility: 'visible',
  }
}

const room = computed(() => findRoom(props.scheduledEvent.item.pServed))
const { canIEditRoomInfo } = useModeratedRoom(room)

const isScheduleUpdated = computed(() => props.scheduledEvent.item.type === 'ConferenceScheduleUpdated')

const editorDisplayName = computed(() => {
  const executor = props.scheduledEvent.item.executor
  if (!executor) {
    return t('common.unknownUser')
  }
  if (currentUser.value?.imLogin === executor) {
    return currentUser.value?.name ?? t('common.unknownUser')
  }
  return findByPServed(executor)?.name ?? t('common.unknownUser')
})

const formattedEvent = computed(() => {
  return formatEvents(props.scheduledEvent.item.schedule?.events || [])[0]
})

// Отмена приходит отдельным ConferenceScheduleDisabled (часто без ICS) — помечаем
// исходную карточку create/update, когда UID больше нет среди активных расписаний комнаты.
const isScheduleCancelled = computed(() => {
  if (props.scheduledEvent.item.type === 'ConferenceScheduleDisabled') {
    return true
  }

  const uid = formattedEvent.value?.uid
  const pServed = props.scheduledEvent.item.pServed
  if (!uid || !pServed) {
    return false
  }

  return roomScheduleStore.isEventCancelled(pServed, uid)
})

const eventParticipantsAsContacts = computed<Array<Contact>>(() => {
  return resolveAttendees(formattedEvent.value?.attendees)
})

const participantsNames = computed(() => (
  eventParticipantsAsContacts.value
    .map((contact) => contact.name)
    .filter(Boolean)
    .join(', ')
))

const canEditEvent = computed(() => (
  !isScheduleCancelled.value
  && Boolean(formattedEvent.value?.uid)
  && canIEditRoomInfo.value
))

const copyLink = async () => {
  const link = formattedEvent.value?.location
  if (!link) {
    return
  }

  try {
    await navigator.clipboard.writeText(link)
  } catch (error) {
    console.error(error)
    return
  }

  isLinkCopied.value = true
  await nextTick()
  updateLinkCopiedTooltipPosition()
  if (copiedTimeout) {
    clearTimeout(copiedTimeout)
  }
  copiedTimeout = setTimeout(() => {
    isLinkCopied.value = false
    copiedTimeout = undefined
  }, 2000)
}

onBeforeUnmount(() => {
  if (copiedTimeout) {
    clearTimeout(copiedTimeout)
  }
})

const onEditClick = () => {
  const event = formattedEvent.value
  if (!event || !canEditEvent.value) {
    return
  }
  emit('edit', event)
}

</script>
